<script setup>
// §8.1 SideNav item. Active state carries FOUR redundant signals, because the
// brass left rail alone measures 1.9:1 and cannot carry the state:
//   brass-100 fill + 600 weight + ink text + brass-600 icon + brass-500 rail.
// The rail is redundant reinforcement, not the sole signal.
defineProps({
	active: { type: Boolean, default: false },
	as: { type: String, default: 'button' }, // button | a | router-link
	to: { type: String, default: null },
})
</script>

<template>
	<component 
		:is="as" 
		:to="to" 
		:href="as === 'a' ? to : undefined" 
		class="nv" 
		:class="{ on: active }"
		:aria-current="active ? 'page' : undefined" 
		:type="as === 'button' ? 'button' : undefined"
	>
		<span v-if="$slots.icon" class="ic">
			<slot name="icon" />
		</span>
		<span class="lbl">
			<slot />
		</span>
		<span v-if="$slots.trailing" class="tr">
			<slot name="trailing" />
		</span>
	</component>
</template>

<style scoped>
.nv
{
	display: flex;
	align-items: center;
	gap: 11px;
	padding: 9px 12px;
	border-radius: var(--r);
	font-family: var(--font-ui);
	font-size: var(--text-nav);
	font-weight: 500;
	color: var(--text-2);
	position: relative;
	width: 100%;
	text-align: left;
	min-height: var(--target-min);
	background: none;
	border: 0;
	cursor: pointer;
	text-decoration: none;

	/* Prevent the navigation links from shrinking below their content size */
	flex-shrink: 0; 
}


/** Except when it's a section-label */
.nv:hover:not(.nv-section-label)
{
	background: var(--surface-sunken);
	color: var(--text);
}


/**
 * Present the item as a "label" in the menu 
 */
.nv-section-label
{
	font-weight: bold; 
	margin-bottom: 10px; 
	text-align: left;
}


.nv-back
{
	/* Same as `.nv:hover`. This is for the "back to portal" button in admin section.  */
	background: var(--surface-sunken);
	color: var(--text);
}

.ic
{
	display: inline-flex;
	flex: none;
	color: var(--icon);
	font-size: 16px;
	/* icon glyph size (FA icons scale with font-size) */
	width: 17px;
	justify-content: center;
}

.lbl
{
	flex: 1;
}

.tr
{
	display: inline-flex;
}

/* active — four redundant signals */
.nv.on
{
	font-weight: 600;
	color: var(--text);
	background: var(--accent-fill);
}

.nv.on .ic
{
	color: var(--accent-edge);
}

.nv.on::before
{
	content: '';
	position: absolute;
	left: -10px;
	top: 7px;
	bottom: 7px;
	width: 3px;
	border-radius: 0 3px 3px 0;
	background: var(--accent);
}
</style>
